// Schemas and types for the cloud tool, shared by server and UI.
import { z } from 'zod';

export const CLOUD_PROVIDERS = ['google', 'dropbox'] as const;
export type CloudProvider = (typeof CLOUD_PROVIDERS)[number];
export const cloudProviderParam = z.object({ provider: z.enum(CLOUD_PROVIDERS) });

export interface CloudProviderStatus {
  provider: CloudProvider;
  /** The server has an OAuth client for this provider. */
  configured: boolean;
  connected: boolean;
  /** E-mail or display name of the connected account. */
  account: string | null;
  /** The granted permission covers writing; false for read-only providers and for connections made before write access existed. */
  canWrite: boolean;
}

export interface CloudStatus {
  providers: CloudProviderStatus[];
}

/**
 * Browsers finish the connection in a popup that shares their session. Native shells cannot: the
 * provider page opens in the system browser, which has no session, so the person types the short
 * code shown in the app into the page that browser lands on (like signing in a TV).
 */
export const cloudStartInput = z.object({ native: z.boolean().optional() });
export type CloudStartInput = z.infer<typeof cloudStartInput>;

export interface CloudStartResponse {
  url: string;
  /** Native flow only: identifies the attempt for status polling. */
  state?: string;
  /** Native flow only: shown in the app, typed into the browser page to prove it is the same person. */
  userCode?: string;
}

export const cloudConfirmInput = z.object({
  state: z.string().min(1).max(200),
  userCode: z.string().min(1).max(40),
});
export type CloudConfirmInput = z.infer<typeof cloudConfirmInput>;

export const cloudFlowQuery = z.object({ state: z.string().min(1).max(200) });

/** Where a native connection attempt stands, for the app that started it. */
export type CloudFlowState = 'waiting' | 'authorized' | 'done' | 'failed' | 'expired';
export interface CloudFlowStatus {
  status: CloudFlowState;
  /** Error code when status is "failed": "denied" or "exchange_failed". */
  error: string | null;
}

export const cloudListQuery = z.object({
  folder: z.string().min(1).max(1000).optional(),
  cursor: z.string().min(1).max(4000).optional(),
});
export type CloudListQuery = z.infer<typeof cloudListQuery>;

export interface CloudItem {
  id: string;
  name: string;
  kind: 'folder' | 'file';
  /** Unknown for Google Docs, Sheets and Slides, which are exported on import. */
  size: number | null;
  modifiedAt: string | null;
  mime: string | null;
  /** Extension the file is converted to on import, e.g. "docx" for a Google Doc. */
  exportAs: string | null;
}

export interface CloudListing {
  items: CloudItem[];
  /** Pass back as `cursor` to fetch the next page; null on the last page. */
  cursor: string | null;
}

export const cloudImportInput = z.object({
  id: z.string().min(1).max(1000),
  parentId: z.uuid().nullable().optional(),
});
export type CloudImportInput = z.infer<typeof cloudImportInput>;

/** Posted by the OAuth callback page to the window that opened it. */
export const CLOUD_MESSAGE_TYPE = 'omnia:cloud';
export interface CloudCallbackMessage {
  type: typeof CLOUD_MESSAGE_TYPE;
  provider: CloudProvider;
  ok: boolean;
  /** Error code when ok is false: "denied", "invalid_state", "exchange_failed". */
  error: string | null;
}

/** Office formats Google Drive can convert into native Docs, Sheets and Slides on upload. */
export const CLOUD_CONVERTIBLE_EXTENSIONS = ['docx', 'xlsx', 'pptx'] as const;

/** A Dosyalarım file paired with the cloud file it was imported from or uploaded to. */
export interface CloudLink {
  fileId: string;
  provider: CloudProvider;
  remoteId: string;
  remoteName: string;
  /** The remote file's modification time when Omnia last synced; a later time on the provider means it changed there. */
  remoteModified: string | null;
  remoteSize: number | null;
  /** Native Google files are exported on import, so the file cannot be written back in place. */
  exported: boolean;
  linkedAt: string;
}

export const cloudLinksQuery = z.object({ fileId: z.uuid().optional() });

const driveParent = z.string().min(1).max(200).nullable().optional();

export const cloudUploadInput = z.object({
  fileId: z.uuid(),
  /** Drive folder id; omitted means the root of My Drive. */
  parentId: driveParent,
  name: z.string().min(1).max(255).optional(),
  /** Convert docx/xlsx/pptx into a native Google Doc, Sheet or Slides file. */
  convert: z.boolean().optional(),
});
export type CloudUploadInput = z.infer<typeof cloudUploadInput>;

export const cloudSaveInput = z.object({
  fileId: z.uuid(),
  /** Overwrite even though the cloud file changed since the last sync. */
  force: z.boolean().optional(),
});
export type CloudSaveInput = z.infer<typeof cloudSaveInput>;

export const cloudFolderInput = z.object({ name: z.string().min(1).max(255), parentId: driveParent });
export type CloudFolderInput = z.infer<typeof cloudFolderInput>;

export interface CloudUploadResult {
  item: CloudItem;
  link: CloudLink;
}
